import React, { useState } from 'react';
import { X, Save, Sparkles } from 'lucide-react';
import { ArchitectureNode, ArchitectureEdge } from '../types';

interface TemplateCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: ArchitectureNode[];
  edges: ArchitectureEdge[];
  onSave: (template: {
    name: string;
    description: string;
    category: 'web' | 'microservices' | 'serverless' | 'data' | 'kubernetes';
    difficulty: 'beginner' | 'intermediate' | 'advanced';
  }) => Promise<void>;
}

const TemplateCreator: React.FC<TemplateCreatorProps> = ({
  isOpen,
  onClose,
  nodes,
  edges,
  onSave
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'web' | 'microservices' | 'serverless' | 'data' | 'kubernetes'>('web');
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (nodes.length === 0) {
      alert('Cannot save empty template');
      return;
    }

    setIsSaving(true);
    try {
      await onSave({ name: name.trim(), description: description.trim(), category, difficulty });
      // Reset form
      setName('');
      setDescription('');
      setCategory('web');
      setDifficulty('beginner');
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-2xl w-[500px] max-w-[90vw] flex flex-col border-2 border-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-black/10 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Sparkles size={24} className="text-purple-600" />
            <div>
              <h2 className="text-xl font-bold text-black">Save as Template</h2>
              <p className="text-sm text-gray-600 mt-1">Create a reusable architecture template</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Template Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., E-Commerce Platform"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-black"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this architecture pattern..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-black resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-black cursor-pointer"
            >
              <option value="web">Web</option>
              <option value="microservices">Microservices</option>
              <option value="serverless">Serverless</option>
              <option value="data">Data</option>
              <option value="kubernetes">Kubernetes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Difficulty Level
            </label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-black cursor-pointer"
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-600">
            <p><strong>{nodes.length}</strong> components</p>
            <p><strong>{edges.length}</strong> connections</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-black/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-black transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Template
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateCreator;
